import { NavLink, useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { boolean, object, string } from "yup";
import { useTranslation } from "react-i18next";
import Logo from "../../../assets/logo-2MAX-large.png";
import CommonFooter from "../../../components/CommonFooter";
import { useKeepQueryParams } from "../../../router/routerHelpers";
import SxForm from "../../../forms/SxForm";
import SxField from "../../../forms/fields/SxField";
import SxCheckbox from "../../../forms/fields/SxCheckbox";
import SubmitButton from "../../../components/SubmitButton";
import { authService } from "../../../services/auth/authService";

const { registerOrganization } = authService();

const FreeOrganizationRegisterPage = () => {
  const { t } = useTranslation(["auth"]);
  const [searchParams] = useSearchParams();
  const keepQueryParams = useKeepQueryParams();
  const profileShareUid = searchParams.get("uid") ?? undefined;

  const [submitted, setSubmitted] = useState<{
    email: string;
    requiresManualValidation: boolean;
  } | null>(null);

  const initialValues = useMemo(() => {
    return {
      email: searchParams.get("email") ?? "",
      phoneNumber: "",
      legalName: "",
      password: "",
      cguAccepted: false,
    };
  }, [searchParams]);

  const validationSchema = useMemo(() => {
    return object({
      email: string().email().label(t("auth:register.labels.EMAIL")).required(),
      phoneNumber: string().label(t("auth:register.labels.PHONE")).required(),
      legalName: string()
        .label(t("auth:register.labels.LEGAL_NAME"))
        .required(),
      password: string()
        .password()
        .label(t("auth:register.labels.PASSWORD"))
        .required(),
      cguAccepted: boolean()
        .transform((v) => !!v)
        .oneOf([true]),
    });
  }, [t]);

  return (
    <div className="auth-screen">
      <img src={Logo} alt="2 Minutes Max" className="logo" />
      <div className="auth-card">
        <div className="card_body">
          {submitted ? (
            <div className="lblock --txt--center">
              <h2 className="title--40">
                {t("auth:register.free.CHECK_MAILBOX_TITLE")}
              </h2>
              <p className="cblock">
                {t("auth:register.free.CHECK_MAILBOX_SENT")}
              </p>
              <p className="cblock title--30 --txt--dark-blue">
                {submitted.email}
              </p>
              <p className="cblock">
                {submitted.requiresManualValidation
                  ? t("auth:register.free.CHECK_MAILBOX_PUBLIC_DOMAIN")
                  : t("auth:register.free.CHECK_MAILBOX_CLICK")}
              </p>
              <p className="cblock body--30">
                {t("auth:register.free.CHECK_MAILBOX_SPAM_HINT")}
              </p>
            </div>
          ) : (
            <>
              <div className="lblock --txt--center">
                <h2 className="title--40">
                  {t("auth:register.CREATE_YOUR_PROFESSIONAL_ACCOUNT")}
                </h2>
                <p className="body--30">{t("auth:register.free.SUBTITLE")}</p>
              </div>
              <div className="lblock">
                <SxForm
                  initialValues={initialValues}
                  onSubmit={(values) => {
                    return registerOrganization({
                      user: {
                        firstname: "",
                        lastname: "",
                        email: values.email,
                        password: values.password,
                        phoneNumber: values.phoneNumber,
                        usage: null,
                        searchedPost: null,
                      },
                      organization: {
                        legalName: values.legalName,
                        email: values.email,
                        phoneNumber: values.phoneNumber,
                        addressLine1: "",
                        addressLine2: "",
                        postalCode: "",
                        city: "",
                        country: "FR",
                      },
                      profileShareUid,
                    }).then((user) =>
                      setSubmitted({
                        email: values.email,
                        requiresManualValidation:
                          !!user.requiresManualValidation,
                      }),
                    );
                  }}
                  validationSchema={validationSchema}
                  enableReinitialize
                >
                  <SxField
                    name="email"
                    placeholder={t("auth:register.placeholders.EMAIL")}
                  />
                  <SxField
                    name="phoneNumber"
                    placeholder={t("auth:register.placeholders.PHONE")}
                  />
                  <div className="input-tip">
                    {t("auth:register.free.PHONE_TIP")}
                  </div>
                  <SxField
                    name="legalName"
                    placeholder={t("auth:register.placeholders.LEGAL_NAME")}
                  />
                  <SxField
                    name="password"
                    placeholder={t("auth:register.placeholders.PASSWORD")}
                  />
                  <div className="cblock">
                    <SxCheckbox
                      name="cguAccepted"
                      label={
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t("auth:register.ACCEPT_CGU"),
                          }}
                        />
                      }
                    />
                  </div>
                  <div className="lblock --txt--center">
                    <SubmitButton type="submit" className="btn--2">
                      {t("auth:register.free.SUBMIT")}
                    </SubmitButton>
                  </div>
                </SxForm>
              </div>
            </>
          )}
          <div className="cblock --txt--center">
            <span className="auth-cta">
              {t("auth:register.ALREADY_REGISTERED")}
            </span>{" "}
            <NavLink className="link" to={keepQueryParams("/login")}>
              {t("auth:register.LOGIN")}
            </NavLink>
          </div>
        </div>
      </div>
      <CommonFooter />
    </div>
  );
};

export default FreeOrganizationRegisterPage;
